import { useTranslation } from "react-i18next";
import { getNumberFormatter } from "@/lib/utils/getNumberFormatter";
import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";
import {
  ReadDepartmentBasedProjectRates,
  readDepartmentBasedProjectRates,
} from "@/schemas/departmentBasedProjectRates";
import DefaultList from "@/components/DefaultList";
import { DepartmentBasedProjectRatesService } from "../service/departmentBasedProjectRatesService";
import { defaultDictionaryTableFeatures } from "@/containers/dictionaries/defaultDictionaryTableFeatures";
import { Module } from "@/constants";

interface DepartmentBasedProjectRatesDirectoryProps {
  ns: string;
  path: string;
}
export const DepartmentBasedProjectRatesDirectory = ({
  ns,
  path,
}: DepartmentBasedProjectRatesDirectoryProps) => {
  const { t, i18n } = useTranslation();
  const numberFormatter = useMemo(
    () => getNumberFormatter(i18n.language),
    [i18n.language],
  );

  const columnOverrides = useMemo<
    Record<string, ColumnDef<ReadDepartmentBasedProjectRates, number>>
  >(
    () => ({
      rate_student_net: {
        accessorKey: "rate_student_net",
        cell: ({ getValue }) => numberFormatter(getValue()),
      },
      rate_student_tax: {
        accessorKey: "rate_student_tax",
        cell: ({ getValue }) => numberFormatter(getValue()),
      },
      rate_kch_net: {
        accessorKey: "rate_kch_net",
        cell: ({ getValue }) => numberFormatter(getValue()),
      },
      rate_kch_tax: {
        accessorKey: "rate_kch_tax",
        cell: ({ getValue }) => numberFormatter(getValue()),
      },
      rate_zus_net: {
        accessorKey: "rate_zus_net",
        cell: ({ getValue }) => numberFormatter(getValue()),
      },
      rate_zus_tax: {
        accessorKey: "rate_zus_tax",
        cell: ({ getValue }) => numberFormatter(getValue()),
      },
      rate_zus_sick_net: {
        accessorKey: "rate_zus_sick_net",
        cell: ({ getValue }) => numberFormatter(getValue()),
      },
      rate_zus_sick_tax: {
        accessorKey: "rate_zus_sick_tax",
        cell: ({ getValue }) => numberFormatter(getValue()),
      },
      rate_gross: {
        accessorKey: "rate_gross",
        cell: ({ getValue }) => numberFormatter(getValue()),
      },
      settlement_type: {
        accessorKey: "settlement_type",
        cell: ({ getValue }) => {
          const value = getValue().toString();
          return t(`settlementType.${value}`);
        },
      },
    }),
    [t, numberFormatter],
  );

  return (
    <DefaultList<ReadDepartmentBasedProjectRates>
      schema={readDepartmentBasedProjectRates}
      name={ns}
      fetchList={DepartmentBasedProjectRatesService.getList}
      pathBuilder={(row) => `${path}/${row.id}`}
      tableFeatures={{
        ...defaultDictionaryTableFeatures,
        exportSettings: { moduleName: Module.DepartmentBasedProjectRates },
      }}
      columnOverrides={columnOverrides}
    />
  );
};
